import React from 'react';
import { Hash, Info } from 'lucide-react';

const RegistrationIdStep = ({ formData, updateFormData }) => {
    const prefix = formData.registrationIdPrefix || 'REG';
    const startNum = formData.registrationIdStartNumber || 1;
    const exampleId = `${prefix}-${startNum}`;

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration ID Configuration</h2>
                <p className="text-gray-600">
                    Set up unique registration IDs for your participants
                </p>
            </div>

            {/* Enable Registration ID */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Generate Registration IDs
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Automatically create unique registration IDs for each participant. This helps in tracking and managing registrations.
                        </p>

                        {/* Toggle Switch */}
                        <button
                            type="button"
                            onClick={() => updateFormData({
                                generateRegistrationId: !formData.generateRegistrationId,
                                registrationIdPrefix: !formData.generateRegistrationId ? formData.registrationIdPrefix : undefined
                            })}
                            className={`
                relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${formData.generateRegistrationId ? 'bg-blue-600' : 'bg-gray-300'}
              `}
                        >
                            <span
                                className={`
                  inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                  ${formData.generateRegistrationId ? 'translate-x-9' : 'translate-x-1'}
                `}
                            />
                        </button>

                        <span className="ml-3 text-sm font-medium text-gray-900">
                            {formData.generateRegistrationId ? 'Enabled' : 'Disabled'}
                        </span>
                    </div>

                    <div className="p-3 bg-blue-50 rounded-lg">
                        <Hash className="w-6 h-6 text-blue-600" />
                    </div>
                </div>
            </div>

            {/* Prefix Configuration - Only show if enabled */}
            {formData.generateRegistrationId && (
                <div className="space-y-6">
                    {/* Prefix Input */}
                    <div>
                        <label htmlFor="regPrefix" className="block text-sm font-medium text-gray-700 mb-2">
                            Registration ID Prefix (Optional)
                        </label>
                        <input
                            type="text"
                            id="regPrefix"
                            value={formData.registrationIdPrefix || ''}
                            onChange={(e) => {
                                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                updateFormData({ registrationIdPrefix: value });
                            }}
                            placeholder="e.g., MTM, CONF, TECH24"
                            maxLength={10}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all uppercase"
                        />
                        <p className="mt-2 text-sm text-gray-500">
                            Letters and numbers only. Maximum 10 characters.
                        </p>
                    </div>

                    {/* Start Number Input */}
                    <div>
                        <label htmlFor="regStartNumber" className="block text-sm font-medium text-gray-700 mb-2">
                            Start Number
                        </label>
                        <input
                            type="number"
                            id="regStartNumber"
                            min={1}
                            value={formData.registrationIdStartNumber || 1}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 1) {
                                    updateFormData({ registrationIdStartNumber: val });
                                }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                        <p className="mt-2 text-sm text-gray-500">
                            The first generated ID will be <strong>{prefix}-{startNum}</strong>.
                            If you change this mid-event, the system fills gaps below existing IDs first, then continues above — no duplicates.
                        </p>
                    </div>

                    {/* Count Mode */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Counter Mode</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => updateFormData({ registrationIdCountMode: 'global' })}
                                className={`p-4 rounded-xl border-2 text-left transition-all ${
                                    (formData.registrationIdCountMode || 'global') === 'global'
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <p className="font-semibold text-gray-900 text-sm mb-1">Global Counter</p>
                                <p className="text-xs text-gray-500">
                                    One shared number pool across all categories.<br />
                                    <code className="bg-gray-100 px-1 rounded">{prefix}-BY-1</code>, <code className="bg-gray-100 px-1 rounded">{prefix}-HB-2</code>, <code className="bg-gray-100 px-1 rounded">{prefix}-EX-3</code>…
                                </p>
                            </button>
                            <button
                                type="button"
                                onClick={() => updateFormData({ registrationIdCountMode: 'per-category' })}
                                className={`p-4 rounded-xl border-2 text-left transition-all ${
                                    formData.registrationIdCountMode === 'per-category'
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <p className="font-semibold text-gray-900 text-sm mb-1">Per-Category Counter</p>
                                <p className="text-xs text-gray-500">
                                    Each category has its own independent counter.<br />
                                    <code className="bg-gray-100 px-1 rounded">{prefix}-BY-20</code> and <code className="bg-gray-100 px-1 rounded">{prefix}-EX-20</code> can both exist.
                                </p>
                            </button>
                        </div>
                        {formData.registrationIdCountMode === 'per-category' && (
                            <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                                Set a custom sub-prefix and start number for each category in the <strong>Participant Categories</strong> step (step 4).
                            </p>
                        )}
                    </div>

                    {/* Preview */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                        <div className="flex items-center space-x-3 mb-4">
                            <Info className="w-5 h-5 text-blue-600" />
                            <h4 className="font-semibold text-gray-900">Preview</h4>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm">
                                <span className="text-sm text-gray-600">First ID generated:</span>
                                <code className="px-3 py-1.5 bg-gray-100 rounded-md font-mono text-sm font-semibold text-gray-900">
                                    {exampleId}
                                </code>
                            </div>

                            <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm">
                                <span className="text-sm text-gray-600">Next few IDs:</span>
                                <code className="px-3 py-1.5 bg-gray-100 rounded-md font-mono text-xs text-gray-700">
                                    {prefix}-{startNum}, {prefix}-{startNum + 1}, {prefix}-{startNum + 2}…
                                </code>
                            </div>
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800">How it works</h3>
                                <div className="mt-2 text-sm text-blue-700">
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Each participant gets a unique ID automatically</li>
                                        <li>IDs are sequential and cannot be duplicated</li>
                                        <li>Participants receive their ID via email after registration</li>
                                        <li>Use IDs for check-in and attendance tracking</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Disabled State Info */}
            {!formData.generateRegistrationId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
                        <Hash className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Registration IDs Disabled
                    </h3>
                    <p className="text-sm text-gray-600 max-w-md mx-auto">
                        Enable this feature to automatically generate unique registration IDs for your participants. This is useful for tracking and managing registrations.
                    </p>
                </div>
            )}
        </div>
    );
};

export default RegistrationIdStep;